/**
 * Parseo de teléfonos: de texto crudo a `{ prefix, number }`.
 *
 * Vive en shared porque lo usan el backend (matching de inbound, importadora) y
 * el frontend (validación previa del Excel de importación). No depende de
 * mongoose ni de nada de servidor.
 */

const { COUNTRY_PREFIXES } = require('../constants/country-prefixes');

// Todos los prefijos de país conocidos (para lookup eficiente).
const ALL_PREFIXES = new Set(COUNTRY_PREFIXES.map(p => p.code));
// Prefijos ordenados por longitud desc: al partir un número probamos primero el
// prefijo más largo (evita que "3" case antes que "34", etc.).
const PREFIXES_BY_LEN = [...ALL_PREFIXES].sort((a, b) => b.length - a.length);

const DEFAULT_PREFIX = '34';

// Mismo cap que el update de `routes/debtors.js`, para no aceptar por la
// importadora lo que el editor rechaza.
const MAX_PHONES = 20;

/** Deja solo dígitos y quita el `00` de marcación internacional inicial. */
const toDigits = raw => {
  const digits = String(raw ?? '').replace(/\D/g, '');
  return digits.startsWith('00') ? digits.slice(2) : digits;
};

/**
 * Parte un número internacional crudo en `{ prefix, number }` (dígitos). Quita
 * `+`/`00`, detecta un prefijo de país conocido dejando >=6 dígitos de número;
 * si no lo detecta, asume España.
 *
 * Asume número en formato internacional (con código de país, o +/00). Un número
 * nacional "pelado" (<=9 dígitos, longitud típica ES) se trata como España sin
 * intentar detectar prefijo (evita falsos positivos tipo 600.. → "60").
 *
 * @param {string} raw
 * @returns {{ prefix: string, number: string }}
 */
function splitPhoneNumber(raw) {
  const clean = toDigits(raw);
  if (clean.length <= 9) return { prefix: DEFAULT_PREFIX, number: clean };
  for (const prefix of PREFIXES_BY_LEN) {
    if (clean.startsWith(prefix) && clean.length - prefix.length >= 6) {
      return { prefix, number: clean.slice(prefix.length) };
    }
  }
  return { prefix: DEFAULT_PREFIX, number: clean };
}

/** Trocea una celda del Excel en items no vacíos. Se acepta `;` además de `,`
 * porque el separador de listas de Excel en locale ES suele ser el punto y coma. */
const splitCell = cell =>
  String(cell ?? '')
    .split(/[,;]/)
    .map(item => item.trim())
    .filter(item => item !== '');

/** ¿Es un prefijo de país conocido? (se acepta escrito con o sin `+`) */
const isKnownPrefix = raw => PREFIXES_BY_LEN.includes(String(raw ?? '').replace(/\D/g, ''));

/**
 * Construye un teléfono aplicando el prefijo indicado, quitándolo del número si
 * viene incrustado. Sin prefijo explícito, se infiere del propio número.
 *
 * @param {string} rawNumber
 * @param {string} [rawPrefix]
 * @returns {{ number: string, prefix: string, note: string }}
 */
function buildPhone(rawNumber, rawPrefix) {
  const digits = toDigits(rawNumber);
  const prefix = String(rawPrefix ?? '').replace(/\D/g, '');

  if (!prefix) {
    const inferred = splitPhoneNumber(digits);
    return { number: inferred.number, prefix: inferred.prefix, note: '' };
  }
  // Solo se despega el prefijo si queda un número con cuerpo suficiente: así
  // "34600111222"→600111222 pero un número que casualmente empiece por 34 no se
  // queda mutilado.
  if (digits.startsWith(prefix) && digits.length - prefix.length >= 6) {
    return { number: digits.slice(prefix.length), prefix, note: '' };
  }
  return { number: digits, prefix, note: '' };
}

/**
 * Convierte las dos columnas del Excel de importación en el array `debtorphones`
 * del modelo. Ambas admiten lista separada por comas emparejada POR POSICIÓN
 * (el prefijo i-ésimo es el del teléfono i-ésimo):
 *
 *   country_prefix    | telefono_deudor
 *   "+34, +351"       | "600111222, 911222333"
 *
 * Reglas:
 * - El prefijo de la columna MANDA sobre lo que venga dentro del número.
 * - Un ÚNICO prefijo se aplica a todos los teléfonos de la fila.
 * - Con 2+ prefijos, la cuenta debe cuadrar; si no, la fila se rechaza.
 * - Se ignoran huecos y comas sobrantes y se deduplican repetidos conservando el
 *   orden. El primero es el principal de envío (`debtorphones[0]`).
 *
 * @param {string|number} phonesCell - Celda `telefono_deudor`.
 * @param {string|number} [prefixesCell] - Celda `country_prefix`.
 * @returns {{ phones: Array<{number:string,prefix:string,note:string}>, error: string|null }}
 *   `error` no nulo ⇒ la fila debe rechazarse; `phones` va vacío.
 */
function parsePhoneColumns(phonesCell, prefixesCell) {
  const numbers = splitCell(phonesCell);
  const prefixes = splitCell(prefixesCell);

  if (numbers.length > MAX_PHONES) {
    return {
      phones: [],
      error: `Demasiados teléfonos (${numbers.length}), el máximo es ${MAX_PHONES}`,
    };
  }
  if (prefixes.length > 1 && prefixes.length !== numbers.length) {
    return {
      phones: [],
      error:
        `El número de prefijos (${prefixes.length}) no coincide con el de ` +
        `teléfonos (${numbers.length}). Pon un solo prefijo para todos, o uno por teléfono.`,
    };
  }

  const phones = [];
  const seen = new Set();
  numbers.forEach((rawNumber, i) => {
    const phone = buildPhone(rawNumber, prefixes.length === 1 ? prefixes[0] : prefixes[i]);
    if (!phone.number) return;
    const key = `${phone.prefix}:${phone.number}`;
    if (seen.has(key)) return;
    seen.add(key);
    phones.push(phone);
  });

  return { phones, error: null };
}

module.exports = {
  splitPhoneNumber,
  parsePhoneColumns,
  buildPhone,
  splitCell,
  isKnownPrefix,
  ALL_PREFIXES,
  MAX_PHONES,
  DEFAULT_PREFIX,
};
